import { useState, useMemo, useCallback, useRef, useEffect } from 'react'
import { ContentMetadataText } from '@quebon/shared-learning/components/common/ContentMetadataText'
import { useTranslation } from 'react-i18next'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate } from 'react-router-dom'
import { learnerApi, MyCourseResponse, ModuleProgressInfo } from '@/api/b2b'
import { getEffectiveConfigurationForB2B } from '@/api/configuration'
import { contentApi } from '@/api/content'
import { productVideoActivityApi, ProductVideoActivityStatusResponse } from '@/api/productVideoActivity'
import { aiqApi, AiqStatusResponse } from '@/api/aiq'
import type { LessonConfiguration, ScopedActivityConfig } from '@/types/configuration'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@quebon/ui/components/dialog'
import Hls from 'hls.js'
import {
  BookOpen,
  CheckCircle,
  Clock,
  Calendar,
  Play,
  Loader2,
  Video,
  FileQuestion,
  ChevronRight,
  ChevronDown,
  Target,
  FileText,
  LayoutList,
  Lock,
  Lightbulb,
  Brain,
  Square,
  CheckSquare,
  GraduationCap,
  Sparkles,
  AlertCircle,
} from 'lucide-react'
import clsx from 'clsx'
import { toast } from 'sonner'
import { getContentMetadata } from '@/constants/contentMapping'

// ============================================================================
// Feature Flags
// ============================================================================

// Pre/Post AIQ 배너는 Configuration에서 활성화된 경우에만 표시
// (aiqStatusValues에서 preAiqEnabled/postAiqEnabled 참조)

// ============================================================================
// Utility Functions
// ============================================================================

function getStatusBadge(status: string, t: (key: string) => string) {
  switch (status) {
    case 'completed':
      return (
        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
          <CheckCircle className="w-3.5 h-3.5 mr-1" />
          {t('courses.completed')}
        </span>
      )
    case 'in_progress':
      return (
        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
          <Clock className="w-3.5 h-3.5 mr-1" />
          {t('courses.inProgress')}
        </span>
      )
    case 'planned':
    default:
      return (
        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
          {t('courses.notStarted')}
        </span>
      )
  }
}

function calcEducationHours(totalLessons: number, moduleCount: number, t: (key: string, opts?: Record<string, unknown>) => string): string {
  const totalMinutes = totalLessons * 15 + moduleCount * 20
  if (totalMinutes === 0) return '-'
  const hours = totalMinutes / 60
  return t('courses.hours', { hours: hours % 1 === 0 ? hours : hours.toFixed(1) })
}

function getActionButtonText(course: MyCourseResponse, t: (key: string) => string): string {
  if (course.curriculumStatus === 'completed') return t('courses.reviewAction')
  if (course.curriculumStatus === 'planned') return t('courses.startAction')
  return t('courses.continueAction')
}

// ============================================================================
// Types
// ============================================================================

interface ClassGroup {
  classId: number
  className: string
  classDescription: string | null
  productId: number
  courses: MyCourseResponse[]
}

// ============================================================================
// Video Modal Component
// ============================================================================

interface VideoModalProps {
  videoUrl: string
  title: string
  onClose: () => void
  onVideoEnded?: () => void
}

function VideoModal({ videoUrl, title, onClose, onVideoEnded }: VideoModalProps) {
  const { t } = useTranslation('learner')
  const hlsRef = useRef<Hls | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [progress, setProgress] = useState(0)
  const [isCompleted, setIsCompleted] = useState(false)
  const completionTriggeredRef = useRef(false)
  // Base UI Dialog는 FloatingPortal로 content를 비동기 마운트하므로
  // useRef 대신 callback ref + state로 video element를 추적
  const [videoEl, setVideoEl] = useState<HTMLVideoElement | null>(null)
  const videoRef = useCallback((node: HTMLVideoElement | null) => {
    setVideoEl(node)
  }, [])

  const REQUIRED_WATCH_PERCENT = 90
  const isHls = videoUrl.includes('.m3u8')

  useEffect(() => {
    const video = videoEl
    if (!video || !videoUrl) return

    const handleTimeUpdate = () => {
      if (video.duration > 0) {
        const percent = (video.currentTime / video.duration) * 100
        setProgress(percent)

        if (percent >= REQUIRED_WATCH_PERCENT && !completionTriggeredRef.current) {
          completionTriggeredRef.current = true
          setIsCompleted(true)
          onVideoEnded?.()
        }
      }
    }

    video.addEventListener('timeupdate', handleTimeUpdate)

    if (isHls) {
      // HLS 스트리밍 (m3u8)
      if (Hls.isSupported()) {
        const hls = new Hls()
        hlsRef.current = hls
        hls.loadSource(videoUrl)
        hls.attachMedia(video)
        hls.on(Hls.Events.MANIFEST_PARSED, () => {
          video.play().catch(() => { /* autoplay blocked */ })
        })
        hls.on(Hls.Events.ERROR, (_event, data) => {
          if (data.fatal) {
            setError(t('courses.videoLoadFailed'))
          }
        })
      } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
        // Safari native HLS
        video.src = videoUrl
        video.addEventListener('loadedmetadata', () => {
          video.play().catch(() => { /* autoplay blocked */ })
        })
      } else {
        setError(t('courses.hlsNotSupported'))
      }
    } else {
      // 직접 비디오 URL (mp4, webm 등 - 동영상 자료실)
      video.src = videoUrl
      video.addEventListener('loadedmetadata', () => {
        video.play().catch(() => { /* autoplay blocked */ })
      })
      video.addEventListener('error', () => {
        setError(t('courses.videoLoadFailed'))
      })
    }

    return () => {
      video.removeEventListener('timeupdate', handleTimeUpdate)
      if (hlsRef.current) {
        hlsRef.current.destroy()
        hlsRef.current = null
      }
    }
  }, [videoEl, videoUrl, isHls, onVideoEnded])

  const headerTitle = (
    <div className="flex gap-2 items-center flex-1 min-w-0">
      <span className="text-sm font-medium truncate"><ContentMetadataText text={title} /></span>
      {isCompleted && (
        <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium text-green-600 bg-green-100 rounded-full shrink-0">
          <CheckCircle className="mr-1 w-3 h-3" />
          {t('courses.completed')}
        </span>
      )}
    </div>
  )

  return (
    <Dialog open={true} onOpenChange={(open) => { if (!open) onClose() }}>
      <DialogContent className="sm:max-w-4xl p-0 gap-0 bg-black overflow-hidden [&>[data-slot=dialog-close]]:text-gray-500 [&>[data-slot=dialog-close]]:top-4">
        <DialogHeader className="px-5 py-4 bg-white rounded-t-xl border-b">
          <DialogTitle>{headerTitle}</DialogTitle>
        </DialogHeader>
        <div className="flex justify-center items-center bg-black aspect-video">
          {error ? (
            <div className="p-8 text-center text-white/70">
              <Video className="mx-auto mb-3 w-12 h-12 opacity-50" />
              <p className="text-sm">{error}</p>
            </div>
          ) : (
            <video ref={videoRef} controls className="w-full h-full" />
          )}
        </div>
        {/* 시청 진행률 바 */}
        <div className="h-1 bg-gray-800">
          <div
            className={clsx(
              'h-full transition-all duration-300',
              isCompleted ? 'bg-green-500' : 'bg-blue-500'
            )}
            style={{ width: `${Math.min(progress, 100)}%` }}
          />
        </div>
      </DialogContent>
    </Dialog>
  )
}

// ============================================================================
// Activity Row Component (시작/종료 활동)
// ============================================================================

interface ActivityRowProps {
  activity: ScopedActivityConfig
  position: 'start' | 'end'
  classProductId: number
  displayType?: 'quiz' | 'video'
  onVideoClick?: (videoContentId: number, position: 'start' | 'end', productId: number, classProductId?: number) => Promise<void>
  onVideoClickProductId?: number
  isCompleted?: boolean
  isGatedByVideo?: boolean
}

function ActivityRow({ activity, position, classProductId, displayType, onVideoClick, onVideoClickProductId, isCompleted: externalCompleted, isGatedByVideo = false }: ActivityRowProps) {
  const { t } = useTranslation('learner')
  const navigate = useNavigate()

  const isQuiz = displayType ? displayType === 'quiz' : (activity.types?.includes('quiz') ?? false)
  const isVideo = displayType ? displayType === 'video' : (activity.types?.includes('video') ?? false)
  const isCompleted = externalCompleted ?? false
  const hasVideoContent = isVideo && !!activity.videoContentId
  const isDisabled = (isVideo && !hasVideoContent) || (isQuiz && isGatedByVideo)
  const [isLoading, setIsLoading] = useState(false)

  // 비디오 활동인 경우 제목 가져오기
  const { data: mediaInfo } = useQuery({
    queryKey: ['mediaInfo', activity.videoContentId],
    queryFn: () => contentApi.getMediaStreamInfo(activity.videoContentId!),
    enabled: activity.enabled && isVideo && hasVideoContent,
    staleTime: 30 * 60 * 1000,
  })

  // Keep hooks unconditional when the configured activity is toggled.
  if (!activity.enabled) return null

  const handleClick = async () => {
    if (isLoading) return
    if (isDisabled) {
      if (isQuiz && isGatedByVideo) {
        toast.warning(t('courses.startVideoRequired'))
      }
      return
    }

    if (isQuiz) {
      navigate('/my/aiq')
    } else if (isVideo && hasVideoContent && onVideoClick) {
      setIsLoading(true)
      try {
        await onVideoClick(activity.videoContentId!, position, onVideoClickProductId ?? 0, classProductId)
      } finally {
        setIsLoading(false)
      }
    }
  }

  const videoTitle = mediaInfo?.title
  const positionLabel = position === 'start' ? t('courses.startVideo') : t('courses.endVideo')
  const quizLabel = isGatedByVideo
    ? (position === 'start' ? `Pre-AIQ Test — ${t('courses.startVideoRequired')}` : `Post-AIQ Test — ${t('courses.startVideoRequired')}`)
    : (position === 'start' ? 'Pre-AIQ Test' : 'Post-AIQ Test')
  const videoLabel = isLoading
    ? t('courses.videoLoading')
    : isDisabled
      ? t('courses.videoNotConfigured')
      : videoTitle || t('courses.videoWatch')
  const Icon = isQuiz ? Brain : Video

  // 비디오 타입: 강조 카드 스타일
  if (isVideo) {
    return (
      <button
        onClick={handleClick}
        disabled={isDisabled || isLoading}
        className={clsx(
          'flex items-center gap-4 px-5 py-4 w-full text-left transition-all group',
          isCompleted
            ? 'bg-green-50 hover:bg-green-100/70'
            : 'bg-gradient-to-r from-blue-50 to-indigo-50 hover:from-blue-100/70 hover:to-indigo-100/70',
          isDisabled && 'opacity-50 cursor-not-allowed',
          isLoading && 'opacity-70 cursor-wait'
        )}
      >
        {/* Play 아이콘 */}
        <div className={clsx(
          'flex flex-shrink-0 justify-center items-center w-11 h-11 rounded-xl',
          isCompleted ? 'bg-green-100' : 'bg-blue-100'
        )}>
          {isLoading ? (
            <Loader2 className="w-5 h-5 text-blue-600 animate-spin" />
          ) : isCompleted ? (
            <CheckCircle className="w-5 h-5 text-green-600" />
          ) : (
            <Video className="w-5 h-5 text-blue-600" />
          )}
        </div>

        {/* 텍스트 영역 */}
        <div className="flex-1 min-w-0">
          <div className="flex gap-2 items-center">
            <span className={clsx(
              'inline-flex items-center px-2 py-0.5 text-xs font-bold rounded',
              isCompleted ? 'bg-green-200 text-green-800' : 'bg-blue-200 text-blue-800'
            )}>
              {positionLabel}
            </span>
            {isCompleted && (
              <span className="text-xs font-medium text-green-600">{t('courses.completed')}</span>
            )}
          </div>
          <p className={clsx(
            'mt-1 text-sm font-medium truncate',
            isCompleted ? 'text-green-700' : 'text-gray-800'
          )}>
            <ContentMetadataText text={videoLabel} />
          </p>
        </div>

        {/* 화살표 */}
        {!isDisabled && !isLoading && (
          <ChevronRight className={clsx(
            'flex-shrink-0 w-5 h-5 transition-transform group-hover:translate-x-0.5',
            isCompleted ? 'text-green-400' : 'text-blue-400'
          )} />
        )}
      </button>
    )
  }

  // 퀴즈 타입: 기존 스타일 유지
  return (
    <button
      onClick={handleClick}
      disabled={isDisabled || isLoading}
      className={clsx(
        'flex items-center gap-3 px-5 py-3.5 w-full text-left transition-colors group',
        isDisabled ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-50',
        isLoading && 'opacity-70 cursor-wait'
      )}
    >
      {isCompleted ? (
        <CheckSquare className="flex-shrink-0 w-5 h-5 text-green-500" />
      ) : (
        <Square className={clsx('flex-shrink-0 w-5 h-5', isDisabled ? 'text-gray-200' : 'text-gray-300 group-hover:text-gray-400')} />
      )}
      <div className={clsx(
        'flex flex-shrink-0 justify-center items-center w-9 h-9 rounded-lg',
        isDisabled ? 'bg-gray-100' : 'bg-purple-100'
      )}>
        {isDisabled && isGatedByVideo ? (
          <Lock className="w-4 h-4 text-gray-400" />
        ) : (
          <Icon className={clsx('w-4 h-4', isDisabled ? 'text-gray-400' : 'text-purple-600')} />
        )}
      </div>
      <span className={clsx(
        'flex-1 text-sm font-medium',
        isDisabled ? 'text-gray-400' : isCompleted ? 'text-green-700' : 'text-gray-700'
      )}>
        {quizLabel}
      </span>
      {!isDisabled && !isLoading && (
        <ChevronRight className="flex-shrink-0 w-4 h-4 text-gray-400" />
      )}
    </button>
  )
}

// ============================================================================
// Curriculum Card Component (커리큘럼 카드 - 풍부한 UI)
// ============================================================================

interface CurriculumCardProps {
  course: MyCourseResponse
  onModuleClick: (moduleId: number) => void
  isVideoGated?: boolean
}

function CurriculumCard({ course, onModuleClick, isVideoGated = false }: CurriculumCardProps) {
  const { t } = useTranslation('learner')
  const [isExpanded, setIsExpanded] = useState(() => {
    if (course.isLocked || isVideoGated) return false
    if (course.curriculumStatus === 'completed') return false
    // 미완료 & 잠기지 않은 과정은 펼침 (in_progress 또는 planned)
    return true
  })
  const metadata = getContentMetadata(course.contentId)
  const thumbnailUrl = course.thumbnailUrl ?? metadata.thumbnailUrl
  const curriculumType = course.curriculumType ?? metadata.curriculumType

  const isCompleted = course.curriculumStatus === 'completed'
  const isInProgress = course.curriculumStatus === 'in_progress'

  return (
    <div className="overflow-hidden">
      {/* ── Course Header (항상 보임) ── */}
      <div className="flex flex-col md:flex-row">
        {/* Thumbnail */}
        <div className="relative flex-shrink-0 w-full h-40 bg-gradient-to-br md:w-56 md:h-auto from-primary-300 to-primary-400">
          {thumbnailUrl ? (
            <img src={thumbnailUrl} alt={course.curriculumTitle} className="object-cover w-full h-full" />
          ) : (
            <div className="flex justify-center items-center w-full h-full">
              <BookOpen className="w-12 h-12 text-white/50" />
            </div>
          )}
          {curriculumType && (
            <div className="absolute top-3 left-3">
              <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-white/90 text-gray-700">
                {curriculumType}
              </span>
            </div>
          )}
          {/* Checkbox overlay */}
          <div className="absolute top-3 right-3">
            {isCompleted ? (
              <div className="flex justify-center items-center w-7 h-7 bg-green-500 rounded-full shadow-md">
                <CheckCircle className="w-5 h-5 text-white" />
              </div>
            ) : isInProgress ? (
              <div className="flex justify-center items-center w-7 h-7 bg-blue-500 rounded-full shadow-md">
                <Clock className="w-4 h-4 text-white" />
              </div>
            ) : (
              <div className="w-7 h-7 rounded-full border-2 border-gray-300 shadow-md bg-white/80" />
            )}
          </div>
        </div>

        {/* Course Info */}
        <div className="flex-1 p-5">
          <div className="flex gap-4 justify-between items-start">
            <div className="flex-1">
              <h3 className="text-lg font-bold text-gray-900 line-clamp-2">
                <ContentMetadataText text={course.curriculumTitle || course.courseTitle} />
              </h3>
              {course.learningObjectives && (
                <div className="px-4 py-3 mt-3 rounded-lg border border-blue-100 bg-blue-50/60">
                  <div className="flex items-center gap-1.5 mb-1.5">
                    <Target className="w-4 h-4 text-blue-500" />
                    <span className="text-xs font-medium text-blue-600">{t('courses.learningObjective')}</span>
                  </div>
                  <p className="text-sm leading-relaxed text-gray-800 whitespace-pre-line"><ContentMetadataText text={course.learningObjectives} /></p>
                </div>
              )}
            </div>

            {/* Progress Circle */}
            <div className="flex-shrink-0">
              <div className="relative w-16 h-16">
                <svg className="w-16 h-16 transform -rotate-90">
                  <circle cx="32" cy="32" r="28" stroke="currentColor" strokeWidth="4" fill="none" className="text-gray-200" />
                  <circle
                    cx="32" cy="32" r="28" stroke="currentColor" strokeWidth="4" fill="none"
                    strokeDasharray={`${2 * Math.PI * 28}`}
                    strokeDashoffset={`${2 * Math.PI * 28 * (1 - course.progress.progressPercent / 100)}`}
                    className="transition-all duration-500 text-primary-500"
                    strokeLinecap="round"
                  />
                </svg>
                <div className="flex absolute inset-0 justify-center items-center">
                  <span className="text-sm font-bold text-gray-700">{course.progress.progressPercent}%</span>
                </div>
              </div>
            </div>
          </div>

          {/* Course Meta */}
          <div className="flex flex-wrap gap-3 mt-4">
            <div className="flex items-center gap-2 px-3 py-1.5 bg-gray-50 rounded-lg">
              <Clock className="w-4 h-4 text-gray-400" />
              <span className="text-xs text-gray-500">{t('courses.educationTime')}</span>
              <span className="text-sm font-semibold text-gray-800">{calcEducationHours(course.progress.totalLessons, course.modules.length, t)}</span>
            </div>
            <div className="flex items-center gap-2 px-3 py-1.5 bg-gray-50 rounded-lg">
              <LayoutList className="w-4 h-4 text-gray-400" />
              <span className="text-xs text-gray-500">{t('courses.composition')}</span>
              <span className="flex gap-2 items-center text-sm font-semibold text-gray-800">
                <span className="flex gap-1 items-center"><Video className="w-3.5 h-3.5 text-blue-500" />{course.composition.videoCount}</span>
                <span className="flex gap-1 items-center"><FileQuestion className="w-3.5 h-3.5 text-green-500" />{course.composition.quizCount}</span>
              </span>
            </div>
          </div>

          {/* Action Button */}
          <div className="flex gap-3 items-center mt-4">
            {getStatusBadge(course.curriculumStatus, t)}
            {course.isLocked || isVideoGated ? (
              <div className="inline-flex items-center px-4 py-2 text-sm font-bold text-gray-500 bg-gray-200 rounded-lg cursor-not-allowed">
                <Lock className="mr-2 w-4 h-4" />
                {isVideoGated ? t('courses.startVideoRequired') : t('courses.completePrevCourse')}
              </div>
            ) : (
              <Link
                to={`/learn/${course.classProductId}/${course.contentId}`}
                className="inline-flex items-center px-4 py-2 text-sm font-bold text-white rounded-lg transition-colors bg-primary-600 hover:bg-primary-700"
              >
                <Play className="mr-2 w-4 h-4" />
                {getActionButtonText(course, t)}
              </Link>
            )}

            {/* Expand/Collapse Button */}
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            >
              {isExpanded ? (
                <>
                  <ChevronDown className="w-4 h-4" />
                  <span className="text-xs">{t('courses.collapse')}</span>
                </>
              ) : (
                <>
                  <ChevronRight className="w-4 h-4" />
                  <span className="text-xs">{t('courses.expand')}</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* ── Expanded Content ── */}
      {isExpanded && (
        <>
          {/* Learning Info Cards */}
          {(course.majorContents || course.concepts) && (
            <div className="px-5 py-4 space-y-3 border-t">
              {course.majorContents && (
                <div className="px-4 py-3 rounded-lg border border-amber-100 bg-amber-50/60">
                  <div className="flex items-center gap-1.5 mb-1.5">
                    <FileText className="w-4 h-4 text-amber-500" />
                    <span className="text-xs font-medium text-amber-600">{t('courses.majorContents')}</span>
                  </div>
                  <p className="text-sm leading-relaxed text-gray-800 whitespace-pre-line"><ContentMetadataText text={course.majorContents} /></p>
                </div>
              )}
              {course.concepts && (
                <div className="px-4 py-3 rounded-lg border border-green-100 bg-green-50/60">
                  <div className="flex items-center gap-1.5 mb-1.5">
                    <Lightbulb className="w-4 h-4 text-green-500" />
                    <span className="text-xs font-medium text-green-600">Key Concepts</span>
                  </div>
                  <p className="text-sm leading-relaxed text-gray-800 whitespace-pre-line"><ContentMetadataText text={course.concepts} /></p>
                </div>
              )}
            </div>
          )}

          {/* Module List Section */}
          {course.modules.length > 0 && (
            <div className="border-t">
              {/* Module Section Header */}
              <div className="flex justify-between items-center px-5 py-3 bg-gray-50 border-b">
                <h4 className="text-sm font-semibold text-gray-700">{t('courses.moduleList')}</h4>
                <div className="flex items-center gap-1.5 text-sm text-gray-500">
                  <CheckCircle className="w-3.5 h-3.5 text-gray-400" />
                  <span className="text-xs text-gray-500">{t('courses.overallProgress')}</span>
                  <span className="text-sm font-semibold text-gray-700">
                    {t('courses.lessonCount', { completed: course.progress.completedLessons, total: course.progress.totalLessons })}
                  </span>
                </div>
              </div>

              {/* Module Rows */}
              <div className="divide-y">
                {course.modules.map((module, index) => {
                  const isModuleLocked = module.progressPercent === 0 &&
                    index > 0 &&
                    course.modules[index - 1].progressPercent < 100

                  return (
                    <ModuleRow
                      key={module.id}
                      module={module}
                      onClick={() => {
                        if (course.isLocked || isVideoGated) {
                          toast.warning(isVideoGated ? t('courses.startVideoRequired') : t('courses.completePrevCourse'))
                          return
                        }
                        if (isModuleLocked) {
                          toast.warning(t('courses.learnInOrder'))
                          return
                        }
                        onModuleClick(module.id)
                      }}
                      isLocked={isModuleLocked}
                      isCurriculumLocked={course.isLocked}
                    />
                  )
                })}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}

// ============================================================================
// Module Row Component
// ============================================================================

interface ModuleRowProps {
  module: ModuleProgressInfo
  onClick: () => void
  isLocked?: boolean
  isCurriculumLocked?: boolean
}

function ModuleRow({ module, onClick, isLocked, isCurriculumLocked }: ModuleRowProps) {
  const { t } = useTranslation('learner')
  const isCompleted = module.progressPercent === 100
  const disabled = isLocked || isCurriculumLocked

  return (
    <button
      onClick={disabled ? undefined : onClick}
      className={clsx(
        'flex gap-4 items-center px-5 py-3 w-full text-left transition-colors',
        disabled ? 'cursor-not-allowed' : 'hover:bg-gray-50'
      )}
    >
      {/* Checkbox */}
      {isCompleted ? (
        <CheckSquare className="flex-shrink-0 w-5 h-5 text-green-500" />
      ) : (
        <Square className={clsx('flex-shrink-0 w-5 h-5', disabled ? 'text-gray-200' : 'text-gray-300')} />
      )}

      {/* Module Number */}
      <div className={clsx(
        'flex flex-shrink-0 justify-center items-center w-8 h-8 text-sm font-bold rounded-full',
        isCompleted ? 'text-green-600 bg-green-100' : 'text-gray-600 bg-gray-100'
      )}>
        {module.sequenceOrder}
      </div>

      {/* Module Info */}
      <div className="flex-1 min-w-0">
        <p className={clsx('text-sm font-medium truncate', disabled ? 'text-gray-400' : 'text-gray-800')}>
          <ContentMetadataText text={module.title} />
        </p>
        <p className="text-xs text-gray-500">
          {t('courses.lessonCompleted', { completed: module.completedLessons, total: module.totalLessons })}
        </p>
      </div>

      {/* Progress Bar */}
      <div className="flex-shrink-0 w-24">
        <div className="overflow-hidden h-2 bg-gray-200 rounded-full">
          <div
            className={clsx(
              'h-full rounded-full transition-all duration-300',
              isCompleted ? 'bg-green-500' : 'bg-primary-500'
            )}
            style={{ width: `${module.progressPercent}%` }}
          />
        </div>
        <p className="mt-1 text-xs text-right text-gray-400">{module.progressPercent}%</p>
      </div>

      {/* Arrow or Lock */}
      {disabled ? (
        <Lock className="flex-shrink-0 w-5 h-5 text-gray-300" />
      ) : (
        <ChevronRight className="flex-shrink-0 w-5 h-5 text-gray-400" />
      )}
    </button>
  )
}

// ============================================================================
// Class Section Component (클래스 그룹)
// ============================================================================

interface ClassSectionProps {
  group: ClassGroup
  config: LessonConfiguration | null
  isLoadingConfig: boolean
  onVideoClick: (videoContentId: number, position: 'start' | 'end', productId: number, classProductId?: number) => Promise<void>
  videoActivityStatus: ProductVideoActivityStatusResponse | null
  aiqStatus: AiqStatusResponse | null
}

function ClassSection({ group, config, isLoadingConfig, onVideoClick, videoActivityStatus, aiqStatus }: ClassSectionProps) {
  const { t } = useTranslation('learner')
  const navigate = useNavigate()

  const startActivity = config?.productLearning?.startActivity
  const endActivity = config?.productLearning?.endActivity
  const hasStartActivity = startActivity?.enabled === true
  const hasEndActivity = endActivity?.enabled === true

  // 시작 영상 미시청 시 커리큘럼 학습 차단
  const hasStartVideo = hasStartActivity && (startActivity?.types?.includes('video') ?? false)
  const isVideoGated = hasStartVideo && videoActivityStatus?.startVideo?.status !== 'completed'

  const firstCourse = group.courses[0]
  const firstClassProductId = firstCourse?.classProductId
  const startDate = firstCourse?.startDate
  const deadline = firstCourse?.deadline

  return (
    <div className="space-y-4">
      {/* Class Header */}
      <div className="px-4 py-5 rounded-xl bg-primary-50/70">
        <div className="flex flex-col gap-2 items-center">
          <div className="flex justify-center items-center w-10 h-10 rounded-xl bg-primary-100">
            <GraduationCap className="w-5 h-5 text-primary-600" />
          </div>
          <h2 className="text-xl font-bold text-gray-900">{group.className || t('courses.class')}</h2>
          {group.classDescription && (
            <p className="text-sm text-gray-500">{group.classDescription}</p>
          )}
          <div className="flex items-center gap-1.5 text-xs text-gray-500">
            <LayoutList className="w-3.5 h-3.5" />
            <span>{t('courses.curriculumCount', { count: group.courses.length })}</span>
          </div>
        </div>

        {/* 학습기간 (클래스 레벨) */}
        {(startDate || deadline) && (
          <div className="flex gap-2 justify-center items-center mt-3 text-sm text-gray-500">
            <Calendar className="w-4 h-4 text-gray-400" />
            <span className="text-xs text-gray-500">{t('courses.learningPeriod')}</span>
            <span className="text-sm font-medium text-gray-700">
              {startDate && deadline
                ? `${startDate} ~ ${deadline}`
                : startDate ? `${startDate} ~` : `~ ${deadline}`}
            </span>
          </div>
        )}
      </div>

      {/* Start Activities */}
      {hasStartActivity && startActivity && (
        <div className="overflow-hidden bg-white rounded-xl divide-y shadow-sm">
          {startActivity.types?.includes('video') && (
            <ActivityRow
              activity={startActivity}
              position="start"
              displayType="video"
              classProductId={firstClassProductId}
              onVideoClick={onVideoClick}
              onVideoClickProductId={group.productId}
              isCompleted={videoActivityStatus?.startVideo?.status === 'completed'}
            />
          )}
          {startActivity.types?.includes('quiz') && (
            <ActivityRow
              activity={startActivity}
              position="start"
              displayType="quiz"
              classProductId={firstClassProductId}
              isCompleted={aiqStatus?.preAiq?.status === 'completed'}
              isGatedByVideo={isVideoGated}
            />
          )}
        </div>
      )}

      {/* Curriculum Cards */}
      {group.courses.map((course) => (
        <div key={`${course.classProductId}-${course.contentId}`} className="overflow-hidden bg-white rounded-xl shadow-sm">
          <CurriculumCard
            course={course}
            isVideoGated={isVideoGated}
            onModuleClick={(moduleId) => {
              navigate(`/learn/${course.classProductId}/${course.contentId}?moduleId=${moduleId}`)
            }}
          />
        </div>
      ))}

      {/* End Activities */}
      {hasEndActivity && endActivity && (
        <div className="overflow-hidden bg-white rounded-xl divide-y shadow-sm">
          {endActivity.types?.includes('video') && (
            <ActivityRow
              activity={endActivity}
              position="end"
              displayType="video"
              classProductId={firstClassProductId}
              onVideoClick={onVideoClick}
              onVideoClickProductId={group.productId}
              isCompleted={videoActivityStatus?.endVideo?.status === 'completed'}
            />
          )}
          {endActivity.types?.includes('quiz') && (
            <ActivityRow
              activity={endActivity}
              position="end"
              displayType="quiz"
              classProductId={firstClassProductId}
              isCompleted={aiqStatus?.postAiq?.status === 'completed'}
            />
          )}
        </div>
      )}
    </div>
  )
}

// ============================================================================
// Main MyCourses Component
// ============================================================================

export default function MyCourses() {
  const { t } = useTranslation('learner')
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [videoModal, setVideoModal] = useState<{
    videoUrl: string
    title: string
    mediaId: number
    position?: 'start' | 'end'
    productId?: number
    classProductId?: number
  } | null>(null)

  const { data, isLoading, isError } = useQuery({
    queryKey: ['myCourses'],
    queryFn: () => learnerApi.getMyCourses(),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  })

  const courses = data?.data?.data || []

  // 클래스별로 그룹핑
  const classGroups: ClassGroup[] = useMemo(() => {
    if (courses.length === 0) return []

    const groupMap = new Map<number, ClassGroup>()

    for (const course of courses) {
      const existing = groupMap.get(course.classId)
      if (existing) {
        existing.courses.push(course)
      } else {
        groupMap.set(course.classId, {
          classId: course.classId,
          className: course.className || t('courses.class'),
          classDescription: course.classDescription ?? null,
          productId: course.productId,
          courses: [course],
        })
      }
    }

    return Array.from(groupMap.values())
  }, [courses])

  // 각 클래스의 effective configuration 조회
  const configQueries = useQuery({
    queryKey: ['classConfigurations', classGroups.map(g => `${g.productId}-${g.classId}`)],
    queryFn: async () => {
      const configs: Record<number, LessonConfiguration> = {}
      for (const group of classGroups) {
        try {
          const result = await getEffectiveConfigurationForB2B({
            productId: group.productId,
            classId: group.classId,
          })
          configs[group.classId] = result.configuration
        } catch (e) {
          console.warn(`Configuration 조회 실패 (classId: ${group.classId}):`, e)
        }
      }
      return configs
    },
    enabled: classGroups.length > 0,
    staleTime: 10 * 60 * 1000,
  })

  const configurations = configQueries.data || {}

  // 각 클래스의 영상 활동 상태 조회
  const videoActivityQueries = useQuery({
    queryKey: ['productVideoActivity', classGroups.map(g => `${g.productId}-${g.classId}`)],
    queryFn: async () => {
      const statuses: Record<number, ProductVideoActivityStatusResponse> = {}
      for (const group of classGroups) {
        try {
          const firstCourse = group.courses[0]
          const result = await productVideoActivityApi.getStatus(group.productId, firstCourse?.classProductId)
          statuses[group.classId] = result.data.data
        } catch (e) {
          console.warn(`Video activity status 조회 실패 (classId: ${group.classId}):`, e)
        }
      }
      return statuses
    },
    enabled: classGroups.length > 0,
    staleTime: 5 * 60 * 1000,
  })

  const videoActivityStatuses = videoActivityQueries.data || {}

  // 각 클래스의 AIQ 상태 조회
  const aiqStatusQueries = useQuery({
    queryKey: ['aiqStatuses', classGroups.map(g => `${g.productId}-${g.classId}`)],
    queryFn: async () => {
      const statuses: Record<number, AiqStatusResponse> = {}
      for (const group of classGroups) {
        try {
          const firstCourse = group.courses[0]
          const result = await aiqApi.getStatus(group.productId, firstCourse?.classProductId)
          statuses[group.classId] = result.data.data
        } catch (e) {
          console.warn(`AIQ status 조회 실패 (classId: ${group.classId}):`, e)
        }
      }
      return statuses
    },
    enabled: classGroups.length > 0,
    staleTime: 5 * 60 * 1000,
  })

  const aiqStatuses = aiqStatusQueries.data || {}

  // 페이지 레벨 AIQ 상태 (전체 클래스 그룹 기준)
  const aiqStatusValues = Object.values(aiqStatuses)
  const hasPreAiqEnabled = aiqStatusValues.some(s => s.preAiqEnabled)
  const hasPostAiqEnabled = aiqStatusValues.some(s => s.postAiqEnabled)
  const hasPreAiqRequired = aiqStatusValues.some(s => s.preAiqEnabled && s.canStartLearning === false)
  const hasPostAiqAvailable = aiqStatusValues.some(s => s.postAiqEnabled && s.canStartPostAiq && s.postAiq?.status !== 'completed')
  const hasPostAiqCompleted = aiqStatusValues.some(s => s.postAiqEnabled && s.postAiq?.status === 'completed')

  const handleVideoEnded = useCallback(() => {
    if (videoModal) {
      const { position, productId, classProductId } = videoModal
      if (position && productId) {
        productVideoActivityApi.complete(productId, position, classProductId)
          .then(() => {
            queryClient.invalidateQueries({ queryKey: ['productVideoActivity'] })
            toast.success(t('courses.videoCompleted'))
          })
          .catch((e) => {
            console.error('비디오 완료 처리 실패:', e)
            toast.error(t('courses.videoCompleteFailed'))
          })
      }
    }
  }, [videoModal, queryClient])

  const handleVideoClick = useCallback(async (videoContentId: number, position?: 'start' | 'end', productId?: number, classProductId?: number) => {
    try {

      // 동영상 자료실(media_library)에서 스트리밍 정보 조회
      const mediaInfo = await contentApi.getMediaStreamInfo(videoContentId)
      if (!mediaInfo.streamUrl) {
        toast.error(t('courses.videoUrlFailed'))
        return
      }

      // 모달 열기
      setVideoModal({
        videoUrl: mediaInfo.streamUrl,
        title: mediaInfo.title || t('courses.video'),
        mediaId: videoContentId,
        position,
        productId,
        classProductId,
      })
    } catch (e) {
      console.error('비디오 로드 실패:', e)
      toast.error(t('courses.videoLoadError'))
    }
  }, [])

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
      </div>
    )
  }

  if (isError) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-red-500">{t('courses.loadError')}</div>
      </div>
    )
  }

  return (
    <div className="space-y-8">
      {/* Pre-AIQ 미완료 안내 */}
      {hasPreAiqEnabled && hasPreAiqRequired && (
        <div className="flex flex-col gap-3 items-center p-5 bg-amber-50 rounded-xl border border-amber-200 sm:flex-row sm:justify-between">
          <div className="flex gap-3 items-center">
            <div className="flex justify-center items-center w-10 h-10 bg-amber-100 rounded-xl">
              <AlertCircle className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="font-semibold text-amber-700">{t('courses.preAiqRequired')}</p>
              <p className="text-sm text-amber-600">{t('courses.preAiqRequiredDesc')}</p>
            </div>
          </div>
          <button
            onClick={() => navigate('/my/aiq')}
            className="flex gap-2 items-center px-5 py-2 font-medium text-white bg-amber-600 rounded-lg transition-colors shrink-0 hover:bg-amber-700"
          >
            <AlertCircle className="w-4 h-4" />
            {t('courses.goToTest')}
          </button>
        </div>
      )}

      {/* Post-AIQ 응시 가능 안내 */}
      {hasPostAiqEnabled && hasPostAiqAvailable && (
        <div className="flex flex-col gap-3 items-center p-5 bg-blue-50 rounded-xl border border-blue-200 sm:flex-row sm:justify-between">
          <div className="flex gap-3 items-center">
            <div className="flex justify-center items-center w-10 h-10 bg-blue-100 rounded-xl">
              <Sparkles className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="font-semibold text-blue-700">{t('courses.postAiqAvailable')}</p>
              <p className="text-sm text-blue-600">{t('courses.postAiqAvailableDesc')}</p>
            </div>
          </div>
          <button
            onClick={() => navigate('/my/aiq')}
            className="flex gap-2 items-center px-5 py-2 font-medium text-white bg-blue-600 rounded-lg transition-colors shrink-0 hover:bg-blue-700"
          >
            <Sparkles className="w-4 h-4" />
            {t('courses.goToTest')}
          </button>
        </div>
      )}

      {/* Post-AIQ 완료 안내 */}
      {hasPostAiqEnabled && hasPostAiqCompleted && (
        <div className="flex gap-3 items-center p-4 bg-emerald-50 rounded-xl border border-emerald-200">
          <CheckCircle className="w-5 h-5 text-emerald-600 shrink-0" />
          <p className="text-sm font-medium text-emerald-700">
            {t('courses.postAiqCompleted')}
          </p>
        </div>
      )}

      {classGroups.length === 0 ? (
        <div className="p-12 text-center bg-white rounded-xl shadow-sm">
          <BookOpen className="mx-auto w-16 h-16 text-gray-300" />
          <h3 className="mt-4 text-lg font-medium text-gray-900">{t('courses.noCourses')}</h3>
          <p className="mt-2 text-gray-500">{t('courses.noCoursesDesc')}</p>
        </div>
      ) : (
        <div className="space-y-10">
          {classGroups.map((group) => (
            <ClassSection
              key={group.classId}
              group={group}
              config={configurations[group.classId] || null}
              isLoadingConfig={configQueries.isLoading}
              onVideoClick={handleVideoClick}
              videoActivityStatus={videoActivityStatuses[group.classId] || null}
              aiqStatus={aiqStatuses[group.classId] || null}
            />
          ))}
        </div>
      )}

      {/* Video Modal */}
      {videoModal && (
        <VideoModal
          videoUrl={videoModal.videoUrl}
          title={videoModal.title}
          onClose={() => setVideoModal(null)}
          onVideoEnded={handleVideoEnded}
        />
      )}
    </div>
  )
}
